import { create } from "zustand";
import type {
  BoardSnapshot,
  Client,
  Comment,
  CreateTaskInput,
  Priority,
  Stage,
  Subtask,
  Task,
  User,
  Annotation,
  CreateAnnotationInput,
  BoardText,
  CreateBoardTextInput,
  UpdateBoardTextInput,
  BoardImage,
  CreateAttachmentInput,
  UpdateImageInput,
  RecycleKind,
  Worklist,
  TaskWorklist,
} from "@board/shared";
import { ApiError, api } from "./api";

/**
 * The tools are the objects lying in the tray. Picking one up selects it;
 * putting it back returns to "select". Every tray object is now active.
 */
export type Tool = "select" | "pen" | "eraser" | "sticky" | "text" | "attach";
export type BoardSelection = { kind: "text" | "attachment"; id: string } | null;

type HistoryEntry = {
  label: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
};

/**
 * One pen, four colours. They were four separate objects in the rail, which was
 * four times the clutter for a choice you make once — the pen now carries the
 * colour it is loaded with, and right-clicking it changes that.
 */
export const MARKER_COLORS = [
  { id: "black", label: "Black", color: "#23262a" },
  { id: "red", label: "Red", color: "#c8382f" },
  { id: "blue", label: "Blue", color: "#2160c4" },
  { id: "green", label: "Green", color: "#1e8a52" },
] as const;

export type MarkerColor = (typeof MARKER_COLORS)[number]["id"];

export function colorOf(id: MarkerColor): string {
  return MARKER_COLORS.find((m) => m.id === id)?.color ?? MARKER_COLORS[0].color;
}

type BoardState = {
  status: "idle" | "loading" | "ready" | "error";
  me: User | null;
  users: User[];
  clients: Client[];
  worklists: Worklist[];
  taskWorklists: TaskWorklist[];
  tasks: Task[];
  subtasks: Subtask[];
  comments: Comment[];
  annotations: Annotation[];
  textItems: BoardText[];
  images: BoardImage[];
  recycleCount: number;

  tool: Tool;
  /** The colour the one pen is loaded with. Independent of whether it is in hand. */
  markerColor: MarkerColor;
  /** Zone the user is acting on — drives the header treatment. */
  selectedClientId: string | null;
  /** The note opened up for editing, if any. */
  focusedTaskId: string | null;
  /** The client whose workspace fills the screen, if any. */
  focusedClientId: string | null;
  selectedObject: BoardSelection;
  historyPast: HistoryEntry[];
  historyFuture: HistoryEntry[];
  historyBusy: boolean;
  historyProblem: string;

  load: () => Promise<void>;
  refresh: () => Promise<void>;
  setTool: (tool: Tool) => void;
  setMarkerColor: (id: MarkerColor) => void;
  select: (clientId: string | null) => void;
  openTask: (taskId: string | null) => void;
  focusClient: (clientId: string | null) => void;
  selectObject: (selection: BoardSelection) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;

  createClient: (input: NewClient) => Promise<Client>;
  /**
   * Applies the change locally first so dragging a zone feels immediate, then
   * reconciles with what the server actually stored. On refusal — a member
   * moving a zone, say — the local guess is rolled back.
   */
  updateClient: (id: string, patch: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  /** Replace a client's logo. `data` is a data URL, already shrunk by the picker. */
  setClientLogo: (id: string, data: string) => Promise<void>;
  /** Take a client's logo off entirely. */
  clearClientLogo: (id: string) => Promise<void>;

  createWorklist: (name: string) => Promise<Worklist>;
  updateWorklist: (id: string, patch: Partial<Worklist>) => Promise<void>;
  setWorklistLogo: (id: string, data: string) => Promise<void>;
  clearWorklistLogo: (id: string) => Promise<void>;
  linkTaskToWorklist: (taskId: string, worklistId: string, stage: Stage) => Promise<void>;
  unlinkTaskFromWorklist: (taskId: string, worklistId: string) => Promise<void>;

  createTask: (input: NewTask) => Promise<void>;
  updateTask: (id: string, patch: Partial<Task>) => Promise<void>;
  moveTaskStage: (id: string, stage: Stage) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;

  addSubtask: (taskId: string, title: string) => Promise<void>;
  updateSubtask: (id: string, patch: Partial<Subtask>) => Promise<void>;
  deleteSubtask: (id: string) => Promise<void>;

  addComment: (taskId: string, body: string) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;
  createAnnotation: (input: CreateAnnotationInput) => Promise<Annotation | null>;
  deleteAnnotation: (id: string) => Promise<void>;
  drawAnnotation: (input: CreateAnnotationInput) => Promise<void>;
  eraseAnnotation: (id: string) => Promise<void>;
  createBoardText: (input: CreateBoardTextInput) => Promise<BoardText | null>;
  updateBoardText: (id: string, patch: UpdateBoardTextInput) => Promise<void>;
  deleteBoardText: (id: string) => Promise<void>;
  placeBoardText: (input: CreateBoardTextInput) => Promise<void>;
  changeBoardText: (id: string, patch: UpdateBoardTextInput, label?: string) => Promise<void>;
  removeBoardText: (id: string) => Promise<void>;
  createAttachment: (input: CreateAttachmentInput) => Promise<void>;
  updateAttachment: (id: string, patch: UpdateImageInput) => Promise<void>;
  deleteAttachment: (id: string) => Promise<void>;
  transformAttachment: (id: string, patch: UpdateImageInput, label?: string) => Promise<void>;
  restoreRecycleItem: (kind: RecycleKind, id: string) => Promise<void>;

  isAdmin: () => boolean;
};

/** What the UI supplies when adding a client; the slot and slug are the server's. */
export type NewClient = { name: string; status?: string | null; slack_channel_id?: string | null };

/** What the UI supplies when placing a sticky; created_by comes from the session. */
export type NewTask = {
  client_id?: string | null;
  worklist_id?: string | null;
  title: string;
  stage: Stage;
  priority?: Priority;
  assignee_id?: string | null;
  due_date?: string | null;
};

/**
 * Undo and redo, one at a time and in order.
 *
 * Each step is a server write, and it used to be that a press arriving while
 * one was in flight was dropped on the floor — `if (historyBusy) return`, plus
 * a disabled button. That reads as a broken keyboard: tapping Ctrl+Z three
 * times to go back three steps went back one, and the faster you tapped the
 * fewer landed. Locally the window is a few milliseconds; against a server over
 * the network it is the whole round trip.
 *
 * So presses queue onto a chain instead. `historyBusy` survives only as
 * something the UI can show — it no longer decides whether a press counts.
 *
 * A step that fails abandons the ones queued behind it. They were computed
 * against a board state that never arrived, and applying them anyway would
 * walk the board somewhere nobody asked for. The queue reopens once it drains.
 */
let historyChain: Promise<void> = Promise.resolve();
let historyQueued = 0;
let historyAbandoned = false;

type HistoryStep = {
  take: (state: BoardState) => HistoryEntry | undefined;
  apply: (entry: HistoryEntry) => Promise<void>;
  settle: (state: BoardState, entry: HistoryEntry) => Partial<BoardState>;
  verb: "undo" | "redo";
};

function runHistoryStep(
  set: (partial: Partial<BoardState> | ((state: BoardState) => Partial<BoardState>)) => void,
  get: () => BoardState,
  step: HistoryStep,
): Promise<void> {
  historyQueued += 1;
  historyChain = historyChain.then(async () => {
    // Read the entry here, not when the key was pressed: by now the steps ahead
    // of this one in the queue have already moved the stacks.
    const entry = historyAbandoned ? undefined : step.take(get());
    if (!entry) return;

    set({ historyBusy: true, historyProblem: "" });
    try {
      await step.apply(entry);
      set((state) => ({ ...step.settle(state, entry), historyBusy: false }));
    } catch {
      historyAbandoned = true;
      set({
        historyBusy: false,
        historyProblem: `Couldn’t ${step.verb} “${entry.label}”.`,
      });
    }
  });

  const settled = historyChain.finally(() => {
    historyQueued -= 1;
    if (historyQueued === 0) historyAbandoned = false;
  });
  return settled;
}

export const useBoard = create<BoardState>((set, get) => ({
  status: "idle",
  me: null,
  users: [],
  clients: [],
  worklists: [],
  taskWorklists: [],
  tasks: [],
  subtasks: [],
  comments: [],
  annotations: [],
  textItems: [],
  images: [],
  recycleCount: 0,
  tool: "select",
  markerColor: "black",
  selectedClientId: null,
  focusedTaskId: null,
  focusedClientId: null,
  selectedObject: null,
  historyPast: [],
  historyFuture: [],
  historyBusy: false,
  historyProblem: "",

  load: async () => {
    set({ status: "loading" });
    try {
      const snapshot: BoardSnapshot = await api.board();
      set({
        status: "ready",
        me: snapshot.me,
        users: snapshot.users,
        clients: snapshot.clients,
        worklists: snapshot.worklists,
        taskWorklists: snapshot.task_worklists,
        tasks: snapshot.tasks,
        subtasks: snapshot.subtasks,
        comments: snapshot.comments,
        annotations: snapshot.annotations,
        textItems: snapshot.text_items,
        images: snapshot.images,
        recycleCount: snapshot.recycle_count,
      });
    } catch {
      set({ status: "error" });
    }
  },

  refresh: async () => {
    try {
      const snapshot: BoardSnapshot = await api.board();
      set({
        me: snapshot.me,
        users: snapshot.users,
        clients: snapshot.clients,
        worklists: snapshot.worklists,
        taskWorklists: snapshot.task_worklists,
        tasks: snapshot.tasks,
        subtasks: snapshot.subtasks,
        comments: snapshot.comments,
        annotations: snapshot.annotations,
        textItems: snapshot.text_items,
        images: snapshot.images,
        recycleCount: snapshot.recycle_count,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        window.location.href = "/";
        return;
      }
      // EventSource reconnects by itself. Keep the last good board in place;
      // a later event or reconnect will retry without flashing an error screen.
    }
  },

  setTool: (tool) => set({ tool, ...(tool === "select" ? null : { selectedObject: null }) }),

  // Loading a colour does not take the pen out of the rail: you can change what
  // it is loaded with without committing to drawing.
  setMarkerColor: (markerColor) => set({ markerColor }),

  select: (selectedClientId) => set({ selectedClientId }),

  openTask: (focusedTaskId) => set({ focusedTaskId }),

  focusClient: (focusedClientId) =>
    // Focusing a client selects it too, so leaving focus mode does not land on
    // a board where nothing is selected and the eye has nowhere to go back to.
    set({ focusedClientId, selectedClientId: focusedClientId, focusedTaskId: null, selectedObject: null }),

  selectObject: (selectedObject) => set({ selectedObject }),

  // Both of these hand their work to runHistoryStep, which queues rather than
  // refuses. Undoing three things is three taps of Ctrl+Z in about a second,
  // and every one of them lands.
  undo: () =>
    runHistoryStep(set, get, {
      take: (state) => state.historyPast.at(-1),
      apply: (entry) => entry.undo(),
      settle: (state, entry) => ({
        historyPast: state.historyPast.slice(0, -1),
        historyFuture: [...state.historyFuture, entry],
      }),
      verb: "undo",
    }),

  redo: () =>
    runHistoryStep(set, get, {
      take: (state) => state.historyFuture.at(-1),
      apply: (entry) => entry.redo(),
      settle: (state, entry) => ({
        historyPast: [...state.historyPast, entry].slice(-50),
        historyFuture: state.historyFuture.slice(0, -1),
      }),
      verb: "redo",
    }),

  createClient: async (input) => {
    const { client } = await api.clients.create(input);
    set((s) => ({ clients: [...s.clients, client] }));
    return client;
  },

  updateClient: async (id, patch) => {
    const before = get().clients.find((c) => c.id === id);
    if (!before) return;
    set((s) => ({ clients: s.clients.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
    try {
      const { client } = await api.clients.update(id, patch as never);
      set((s) => ({ clients: s.clients.map((c) => (c.id === id ? client : c)) }));
    } catch (error) {
      set((s) => ({ clients: s.clients.map((c) => (c.id === id ? before : c)) }));
      throw error;
    }
  },

  deleteClient: async (id) => {
    const removedTaskIds = new Set(get().tasks.filter((task) => task.client_id === id).map((task) => task.id));
    await api.clients.remove(id);
    set((s) => ({
      clients: s.clients.filter((c) => c.id !== id),
      tasks: s.tasks.filter((t) => t.client_id !== id),
      taskWorklists: s.taskWorklists.filter((link) => !removedTaskIds.has(link.task_id)),
      selectedClientId: s.selectedClientId === id ? null : s.selectedClientId,
      // Nothing may stay focused on a client that no longer exists.
      focusedClientId: s.focusedClientId === id ? null : s.focusedClientId,
      focusedTaskId: null,
      recycleCount: s.recycleCount + 1,
    }));
  },

  setClientLogo: async (id, data) => {
    // No optimistic step: the row that comes back carries the new
    // logo_updated_at, which is the cache key the <img> is keyed on. Guessing
    // it locally would fetch a logo the server has not stored yet.
    const { client } = await api.clients.setLogo(id, data);
    set((s) => ({ clients: s.clients.map((c) => (c.id === id ? client : c)) }));
  },

  clearClientLogo: async (id) => {
    const { client } = await api.clients.clearLogo(id);
    set((s) => ({ clients: s.clients.map((c) => (c.id === id ? client : c)) }));
  },

  createWorklist: async (name) => {
    const { worklist } = await api.worklists.create({ name });
    set((state) => ({ worklists: [...state.worklists, worklist] }));
    return worklist;
  },

  updateWorklist: async (id, patch) => {
    const before = get().worklists.find((item) => item.id === id);
    if (!before) return;
    set((state) => ({
      worklists: state.worklists.map((item) => item.id === id ? { ...item, ...patch } : item),
    }));
    try {
      const { worklist } = await api.worklists.update(id, patch as never);
      set((state) => ({ worklists: state.worklists.map((item) => item.id === id ? worklist : item) }));
    } catch (error) {
      set((state) => ({ worklists: state.worklists.map((item) => item.id === id ? before : item) }));
      throw error;
    }
  },

  setWorklistLogo: async (id, data) => {
    const { worklist } = await api.worklists.setLogo(id, data);
    set((state) => ({ worklists: state.worklists.map((item) => item.id === id ? worklist : item) }));
  },

  clearWorklistLogo: async (id) => {
    const { worklist } = await api.worklists.clearLogo(id);
    set((state) => ({ worklists: state.worklists.map((item) => item.id === id ? worklist : item) }));
  },

  linkTaskToWorklist: async (taskId, worklistId, stage) => {
    const beforeTask = get().tasks.find((task) => task.id === taskId);
    if (!beforeTask) return;
    const existing = get().taskWorklists.find(
      (placement) => placement.task_id === taskId && placement.worklist_id === worklistId,
    );
    set((state) => ({
      tasks: state.tasks.map((task) => task.id === taskId ? { ...task, stage } : task),
    }));
    try {
      const result = await api.worklists.link({ task_id: taskId, worklist_id: worklistId, stage });
      set((state) => ({
        tasks: state.tasks.map((task) => task.id === taskId ? result.task : task),
        taskWorklists: existing || state.taskWorklists.some((item) => item.id === result.task_worklist.id)
          ? state.taskWorklists
          : [...state.taskWorklists, result.task_worklist],
      }));
    } catch (error) {
      set((state) => ({
        tasks: state.tasks.map((task) => task.id === taskId ? beforeTask : task),
      }));
      throw error;
    }
  },

  unlinkTaskFromWorklist: async (taskId, worklistId) => {
    const before = get().taskWorklists.find(
      (item) => item.task_id === taskId && item.worklist_id === worklistId,
    );
    if (!before) return;
    set((state) => ({
      taskWorklists: state.taskWorklists.filter((item) => item.id !== before.id),
    }));
    try {
      await api.worklists.unlink(taskId, worklistId);
    } catch (error) {
      set((state) => ({ taskWorklists: [...state.taskWorklists, before] }));
      throw error;
    }
  },

  createTask: async (input) => {
    const me = get().me;
    if (!me) return;
    // Placed on the board immediately, under a temporary id, so the note appears
    // under the hand that put it there. The server's row replaces it on success.
    const temp: Task = {
      id: `temp-${crypto.randomUUID()}`,
      client_id: input.client_id ?? null,
      worklist_id: input.worklist_id ?? null,
      title: input.title,
      assignee_id: input.assignee_id ?? null,
      due_date: input.due_date ?? null,
      stage: input.stage,
      priority: input.priority ?? "normal",
      created_by: me.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set((s) => ({ tasks: [...s.tasks, temp] }));
    try {
      const { task } = await api.tasks.create(input as CreateTaskInput);
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === temp.id ? task : t)) }));
    } catch (error) {
      set((s) => ({ tasks: s.tasks.filter((t) => t.id !== temp.id) }));
      throw error;
    }
  },

  updateTask: async (id, patch) => {
    const before = get().tasks.find((t) => t.id === id);
    if (!before) return;
    set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    try {
      const { task } = await api.tasks.update(id, patch as never);
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? task : t)) }));
    } catch (error) {
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? before : t)) }));
      throw error;
    }
  },

  moveTaskStage: async (id, stage) => {
    const before = get().tasks.find((task) => task.id === id);
    if (!before || before.stage === stage) return;
    await get().updateTask(id, { stage });
    const entry: HistoryEntry = {
      label: "Move sticky",
      undo: () => get().updateTask(id, { stage: before.stage }),
      redo: () => get().updateTask(id, { stage }),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  deleteTask: async (id) => {
    const before = get().tasks.find((t) => t.id === id);
    if (!before) return;
    const beforeLinks = get().taskWorklists.filter((link) => link.task_id === id);
    set((s) => ({
      tasks: s.tasks.filter((t) => t.id !== id),
      taskWorklists: s.taskWorklists.filter((link) => link.task_id !== id),
      focusedTaskId: s.focusedTaskId === id ? null : s.focusedTaskId,
    }));
    try {
      await api.tasks.remove(id);
      set((s) => ({ recycleCount: s.recycleCount + 1 }));
    } catch (error) {
      // A member binning someone else's sticky gets a 404. Put it back.
      set((s) => ({
        tasks: [...s.tasks, before],
        taskWorklists: [...s.taskWorklists, ...beforeLinks.filter(
          (link) => !s.taskWorklists.some((current) => current.id === link.id),
        )],
      }));
      throw error;
    }
  },

  addSubtask: async (taskId, title) => {
    const position = get().subtasks.filter((s) => s.task_id === taskId).length;
    const { subtask } = await api.subtasks.create({ task_id: taskId, title, position });
    set((s) => ({ subtasks: [...s.subtasks, subtask] }));
  },

  updateSubtask: async (id, patch) => {
    const before = get().subtasks.find((s) => s.id === id);
    if (!before) return;
    // Ticking a box has to feel instant, so it lands locally first.
    set((s) => ({ subtasks: s.subtasks.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
    try {
      const { subtask } = await api.subtasks.update(id, patch as never);
      set((s) => ({ subtasks: s.subtasks.map((x) => (x.id === id ? subtask : x)) }));
    } catch (error) {
      set((s) => ({ subtasks: s.subtasks.map((x) => (x.id === id ? before : x)) }));
      throw error;
    }
  },

  deleteSubtask: async (id) => {
    const before = get().subtasks.find((s) => s.id === id);
    if (!before) return;
    set((s) => ({ subtasks: s.subtasks.filter((x) => x.id !== id) }));
    try {
      await api.subtasks.remove(id);
      set((s) => ({ recycleCount: s.recycleCount + 1 }));
    } catch (error) {
      set((s) => ({ subtasks: [...s.subtasks, before] }));
      throw error;
    }
  },

  addComment: async (taskId, body) => {
    // Not optimistic. A comment that appears and then vanishes because the
    // write failed is worse than one that takes a moment to show up — people
    // reread what they said and assume it sent.
    const { comment } = await api.comments.create({ task_id: taskId, body });
    set((s) => ({ comments: [...s.comments, comment] }));
  },

  deleteComment: async (id) => {
    const before = get().comments.find((c) => c.id === id);
    if (!before) return;
    set((s) => ({ comments: s.comments.filter((c) => c.id !== id) }));
    try {
      await api.comments.remove(id);
      set((s) => ({ recycleCount: s.recycleCount + 1 }));
    } catch (error) {
      // Someone else's comment comes back as a 404. Put it back on screen.
      set((s) => ({ comments: [...s.comments, before] }));
      throw error;
    }
  },

  createAnnotation: async (input) => {
    const me = get().me;
    if (!me) return null;
    const temp: Annotation = {
      id: `temp-${crypto.randomUUID()}`,
      client_id: input.client_id ?? null,
      type: input.type ?? "freehand",
      path_data: input.path_data,
      color: input.color,
      width: input.width,
      created_by: me.id,
      created_at: new Date().toISOString(),
    };
    set((state) => ({ annotations: [...state.annotations, temp] }));
    try {
      const { annotation } = await api.annotations.create(input);
      set((state) => ({ annotations: state.annotations.map(
        (item) => item.id === temp.id ? annotation : item,
      ) }));
      return annotation;
    } catch (error) {
      set((state) => ({ annotations: state.annotations.filter((item) => item.id !== temp.id) }));
      throw error;
    }
  },

  deleteAnnotation: async (id) => {
    const before = get().annotations.find((item) => item.id === id);
    if (!before || id.startsWith("temp-")) return;
    set((state) => ({ annotations: state.annotations.filter((item) => item.id !== id) }));
    try {
      await api.annotations.remove(id);
    } catch (error) {
      set((state) => ({ annotations: [...state.annotations, before] }));
      throw error;
    }
  },

  drawAnnotation: async (input) => {
    const created = await get().createAnnotation(input);
    if (!created) return;
    let current = created;
    const payload = () => ({
      client_id: current.client_id,
      type: current.type,
      path_data: current.path_data,
      color: current.color,
      width: current.width,
    });
    const entry: HistoryEntry = {
      label: "Draw line",
      undo: () => get().deleteAnnotation(current.id),
      redo: async () => {
        const restored = await get().createAnnotation(payload());
        if (restored) current = restored;
      },
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  eraseAnnotation: async (id) => {
    const erased = get().annotations.find((item) => item.id === id);
    if (!erased || id.startsWith("temp-")) return;
    let current = erased;
    await get().deleteAnnotation(id);
    const entry: HistoryEntry = {
      label: "Erase line",
      undo: async () => {
        const restored = await get().createAnnotation({
          client_id: current.client_id, type: current.type, path_data: current.path_data,
          color: current.color, width: current.width,
        });
        if (restored) current = restored;
      },
      redo: () => get().deleteAnnotation(current.id),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  createBoardText: async (input) => {
    const me = get().me;
    if (!me) return null;
    const now = new Date().toISOString();
    const temp: BoardText = {
      id: `temp-${crypto.randomUUID()}`,
      client_id: input.client_id ?? null,
      text: input.text,
      canvas_x: input.canvas_x,
      canvas_y: input.canvas_y,
      color: input.color ?? "#23262a",
      font_size: input.font_size ?? 22,
      created_by: me.id,
      created_at: now,
      updated_at: now,
    };
    set((state) => ({ textItems: [...state.textItems, temp] }));
    try {
      const { text_item } = await api.boardText.create(input);
      set((state) => ({ textItems: state.textItems.map(
        (item) => item.id === temp.id ? text_item : item,
      ) }));
      return text_item;
    } catch (error) {
      set((state) => ({ textItems: state.textItems.filter((item) => item.id !== temp.id) }));
      throw error;
    }
  },

  updateBoardText: async (id, patch) => {
    const before = get().textItems.find((item) => item.id === id);
    if (!before || id.startsWith("temp-")) return;
    set((state) => ({ textItems: state.textItems.map(
      (item) => item.id === id ? { ...item, ...patch } : item,
    ) }));
    try {
      const { text_item } = await api.boardText.update(id, patch);
      set((state) => ({ textItems: state.textItems.map(
        (item) => item.id === id ? text_item : item,
      ) }));
    } catch (error) {
      set((state) => ({ textItems: state.textItems.map(
        (item) => item.id === id ? before : item,
      ) }));
      throw error;
    }
  },

  deleteBoardText: async (id) => {
    const before = get().textItems.find((item) => item.id === id);
    if (!before || id.startsWith("temp-")) return;
    const wasSelected = get().selectedObject?.kind === "text" && get().selectedObject?.id === id;
    set((state) => ({
      textItems: state.textItems.filter((item) => item.id !== id),
      selectedObject: wasSelected ? null : state.selectedObject,
    }));
    try {
      await api.boardText.remove(id);
      set((state) => ({ recycleCount: state.recycleCount + 1 }));
    } catch (error) {
      set((state) => ({
        textItems: [...state.textItems, before],
        selectedObject: wasSelected ? { kind: "text", id } : state.selectedObject,
      }));
      throw error;
    }
  },

  placeBoardText: async (input) => {
    const created = await get().createBoardText(input);
    if (!created) return;
    const current = created;
    const entry: HistoryEntry = {
      label: "Place text",
      undo: () => get().deleteBoardText(current.id),
      redo: () => get().restoreRecycleItem("text", current.id),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  changeBoardText: async (id, patch, label = "Move text") => {
    const before = get().textItems.find((item) => item.id === id);
    if (!before) return;
    const inverse = Object.fromEntries(Object.keys(patch).map((key) => [key, before[key as keyof BoardText]])) as UpdateBoardTextInput;
    await get().updateBoardText(id, patch);
    const entry: HistoryEntry = {
      label,
      undo: () => get().updateBoardText(id, inverse),
      redo: () => get().updateBoardText(id, patch),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  removeBoardText: async (id) => {
    const removed = get().textItems.find((item) => item.id === id);
    if (!removed || id.startsWith("temp-")) return;
    const current = removed;
    await get().deleteBoardText(id);
    const entry: HistoryEntry = {
      label: "Delete text",
      undo: () => get().restoreRecycleItem("text", current.id),
      redo: () => get().deleteBoardText(current.id),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  createAttachment: async (input) => {
    const { attachment } = await api.attachments.create(input);
    set((state) => ({ images: [...state.images, attachment] }));
  },

  updateAttachment: async (id, patch) => {
    const before = get().images.find((item) => item.id === id);
    if (!before) return;
    set((state) => ({ images: state.images.map(
      (item) => item.id === id ? { ...item, ...patch } : item,
    ) }));
    try {
      const { attachment } = await api.attachments.update(id, patch);
      set((state) => ({ images: state.images.map(
        (item) => item.id === id ? attachment : item,
      ) }));
    } catch (error) {
      set((state) => ({ images: state.images.map((item) => item.id === id ? before : item) }));
      throw error;
    }
  },

  transformAttachment: async (id, patch, label = "Move attachment") => {
    const before = get().images.find((item) => item.id === id);
    if (!before) return;
    const inverse = Object.fromEntries(Object.keys(patch).map((key) => [key, before[key as keyof BoardImage]])) as UpdateImageInput;
    await get().updateAttachment(id, patch);
    const entry: HistoryEntry = {
      label,
      undo: () => get().updateAttachment(id, inverse),
      redo: () => get().updateAttachment(id, patch),
    };
    set((state) => ({ historyPast: [...state.historyPast, entry].slice(-50), historyFuture: [] }));
  },

  deleteAttachment: async (id) => {
    const before = get().images.find((item) => item.id === id);
    if (!before) return;
    set((state) => ({ images: state.images.filter((item) => item.id !== id) }));
    try {
      await api.attachments.remove(id);
      set((state) => ({ recycleCount: state.recycleCount + 1 }));
    } catch (error) {
      set((state) => ({ images: [...state.images, before] }));
      throw error;
    }
    if (get().selectedObject?.kind === "attachment" && get().selectedObject?.id === id) {
      set({ selectedObject: null });
    }
  },

  restoreRecycleItem: async (kind, id) => {
    await api.recycleBin.restore(kind, id);
    await get().refresh();
  },

  isAdmin: () => get().me?.role === "admin",
}));
